/*
  Kiwi Ears · product finder (Rene 2026-08-22).

  Faceted filtering over the cards the section already rendered. Nothing is
  fetched and nothing is rebuilt: filtering toggles `hidden`, sorting sets the
  CSS `order`, so an in-flight quick-add survives a filter change and a re-sort
  costs one style pass instead of 38 node replacements.

  The two things that make this usable rather than merely present:

  · COUNTS THAT KNOW ABOUT EACH OTHER. Each option's number is how many
    products you would get if you picked it, with the OTHER groups' selections
    still applied. Standard faceted counting: a group's own selection is
    excluded from its own counts, or picking one option would drop every other
    number in that group to zero and the group would look broken.

  · OPTIONS AT ZERO ARE DISABLED, NOT REMOVED. Hiding them makes the list jump
    under the cursor mid-decision. A greyed row still tells you the filter
    exists and that this combination has nothing.

  Motion, and why each one earns it:
  · the grid re-lays-out through GSAP Flip, because a re-sort should read as the
    same set rearranging, not 38 cards blinking into new slots. Leavers settle
    to 0.96, arrivers come up from it. Never from scale(0).
  · the clear button appears when there is something to clear, 125ms in, faster
    out.
  · nothing else. The count changes on every keystroke, so animating it would
    be noise, and keyboard actions ("/" to focus, Escape to clear) never
    animate. All of it collapses under prefers-reduced-motion.

  State is mirrored into the query string, so a filtered view survives a reload
  and can be pasted to someone else.
*/
(function () {
  var root = document.getElementById('fnd');
  if (!root) return;

  var grid = root.querySelector('#fnd-grid');
  var items = [].slice.call(root.querySelectorAll('.fnd-item'));
  var search = root.querySelector('#fnd-q');
  var clearBtn = root.querySelector('#fnd-clear');
  var sortSel = root.querySelector('#fnd-sort');
  var count = root.querySelector('#fnd-count');
  var railCount = root.querySelector('#fnd-railcount');
  var none = root.querySelector('#fnd-none');
  var chipBar = root.querySelector('#fnd-chips');
  var resets = [].slice.call(root.querySelectorAll('#fnd-reset, #fnd-none-reset'));
  var rail = root.querySelector('#fnd-rail');

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasFlip = window.gsap && window.Flip;
  if (hasFlip) window.gsap.registerPlugin(window.Flip);

  /* ---------- groups, straight off the markup ---------- */
  var groups = [].slice.call(root.querySelectorAll('[data-facet-group]')).map(function (el) {
    return {
      key: el.getAttribute('data-facet-group'),
      label: el.getAttribute('data-facet-label'),
      el: el,
      inputs: [].slice.call(el.querySelectorAll('.fnd-box'))
    };
  });
  var priceGroup = groups.filter(function (g) { return g.key === 'price'; })[0];
  var tagGroups = groups.filter(function (g) { return g.key !== 'price'; });

  var term = '';

  function tokensOf(el, key) {
    var raw = el.getAttribute('data-' + key) || '';
    return raw ? raw.split(/\s+/) : [];
  }

  function selected(group) {
    return group.inputs.filter(function (i) { return i.checked; });
  }

  function priceOf(el) { return parseInt(el.getAttribute('data-price'), 10) / 100; }

  function priceRange() {
    var on = priceGroup ? selected(priceGroup)[0] : null;
    if (!on) return { min: 0, max: Infinity, label: '' };
    var max = on.getAttribute('data-max');
    return {
      min: parseFloat(on.getAttribute('data-min')) || 0,
      max: max === '' ? Infinity : parseFloat(max),
      label: on.getAttribute('data-facet-label')
    };
  }

  /* Does one product satisfy every group except `skip`? */
  function passes(el, skip) {
    if (term !== '' && (el.getAttribute('data-search') || '').indexOf(term) < 0) return false;

    if (skip !== 'price') {
      var r = priceRange();
      var p = priceOf(el);
      if (p < r.min || p > r.max) return false;
    }

    for (var i = 0; i < tagGroups.length; i++) {
      var g = tagGroups[i];
      if (g.key === skip) continue;
      var picks = selected(g);
      if (!picks.length) continue;                 /* nothing chosen: no constraint */
      var have = tokensOf(el, g.key);
      var hit = picks.some(function (input) {
        return have.indexOf(input.getAttribute('data-facet-value')) > -1;
      });
      if (!hit) return false;                      /* OR inside a group, AND across them */
    }
    return true;
  }

  /* ---------- counts ---------- */
  function refreshCounts() {
    tagGroups.forEach(function (g) {
      var pool = items.filter(function (el) { return passes(el, g.key); });
      g.inputs.forEach(function (input) {
        var value = input.getAttribute('data-facet-value');
        var n = pool.filter(function (el) { return tokensOf(el, g.key).indexOf(value) > -1; }).length;
        var label = input.closest('.fnd-opt');
        var out = label && label.querySelector('[data-count]');
        if (out) out.textContent = n;
        /* a checked option is never disabled: you must be able to undo it */
        var dead = n === 0 && !input.checked;
        input.disabled = dead;
        if (label) label.classList.toggle('is-off', dead);
      });
    });

    if (priceGroup) {
      var pool = items.filter(function (el) { return passes(el, 'price'); });
      priceGroup.inputs.forEach(function (input) {
        var min = parseFloat(input.getAttribute('data-min')) || 0;
        var maxRaw = input.getAttribute('data-max');
        var max = maxRaw === '' ? Infinity : parseFloat(maxRaw);
        var n = pool.filter(function (el) {
          var p = priceOf(el);
          return p >= min && p <= max;
        }).length;
        var label = input.closest('.fnd-opt');
        var out = label && label.querySelector('[data-count]');
        if (out) out.textContent = n;
        var dead = n === 0 && !input.checked;
        input.disabled = dead;
        if (label) label.classList.toggle('is-off', dead);
      });
    }
  }

  /* ---------- applied-filter chips ---------- */
  function refreshChips() {
    if (!chipBar) return;
    var active = [];
    tagGroups.forEach(function (g) {
      selected(g).forEach(function (input) {
        active.push({ input: input, label: input.getAttribute('data-facet-label') });
      });
    });
    var r = priceRange();
    if (r.min > 0 || r.max !== Infinity) {
      active.push({ input: selected(priceGroup)[0], label: r.label, price: true });
    }
    if (term !== '') active.push({ term: true, label: '“' + term + '”' });

    chipBar.innerHTML = '';
    chipBar.classList.toggle('hidden', active.length === 0);
    chipBar.classList.toggle('flex', active.length > 0);

    active.forEach(function (a) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'fnd-chip inline-flex items-center gap-2 rounded-full border border-kiwi-deep/35 bg-white px-3.5 py-2 font-sans text-[13px] leading-none text-ink-2 wght-600';
      b.innerHTML = a.label + '<span aria-hidden="true" class="text-ink-2/45">&times;</span>';
      b.setAttribute('aria-label', 'Remove filter ' + a.label);
      b.addEventListener('click', function () {
        if (a.term) { search.value = ''; term = ''; }
        else if (a.price) { priceGroup.inputs[0].checked = true; }
        else { a.input.checked = false; }
        render(true);
      });
      chipBar.appendChild(b);
    });

    resets.forEach(function (r2) {
      r2.classList.toggle('hidden', active.length === 0);
      r2.classList.toggle('flex', active.length > 0 && r2.id === 'fnd-reset');
    });
    if (railCount) railCount.textContent = active.length ? active.length + ' on' : '';
  }

  /* ---------- url ---------- */
  function syncUrl() {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams();
    tagGroups.forEach(function (g) {
      var vals = selected(g).map(function (i) { return i.getAttribute('data-facet-value'); });
      if (vals.length) params.set(g.key, vals.join(','));
    });
    var r = priceRange();
    if (r.min > 0 || r.max !== Infinity) params.set('price', selected(priceGroup)[0].getAttribute('data-facet-value'));
    if (term !== '') params.set('q', term);
    if (sortSel.value !== 'feat') params.set('sort', sortSel.value);
    var qs = params.toString();
    window.history.replaceState(null, '', qs ? '?' + qs : window.location.pathname);
  }

  function readUrl() {
    var params = new URLSearchParams(window.location.search);
    tagGroups.forEach(function (g) {
      var raw = params.get(g.key);
      if (!raw) return;
      var want = raw.split(',');
      g.inputs.forEach(function (i) {
        if (want.indexOf(i.getAttribute('data-facet-value')) > -1) i.checked = true;
      });
    });
    var pr = params.get('price');
    if (pr && priceGroup) {
      priceGroup.inputs.forEach(function (i) {
        if (i.getAttribute('data-facet-value') === pr) i.checked = true;
      });
    }
    var q = params.get('q');
    if (q) { search.value = q; term = q.toLowerCase().trim(); }
    var so = params.get('sort');
    if (so) sortSel.value = so;
    if (rail && (params.toString() !== '')) rail.open = true;
  }

  /* ---------- render ---------- */
  function render(animate) {
    var flipState = (hasFlip && !still && animate)
      ? window.Flip.getState(items, { props: 'opacity' })
      : null;

    var shown = [];
    items.forEach(function (el) {
      var hit = passes(el, null);
      el.classList.toggle('hidden', !hit);
      if (hit) shown.push(el);
    });

    var sort = sortSel.value;
    if (sort === 'pl') shown.sort(function (a, b) { return priceOf(a) - priceOf(b); });
    else if (sort === 'ph') shown.sort(function (a, b) { return priceOf(b) - priceOf(a); });
    else if (sort === 'az') shown.sort(function (a, b) {
      return (a.getAttribute('data-search') || '').localeCompare(b.getAttribute('data-search') || '');
    });
    else shown.sort(function (a, b) { return items.indexOf(a) - items.indexOf(b); });
    shown.forEach(function (el, i) { el.style.order = i + 1; });

    count.textContent = shown.length + (shown.length === 1 ? ' product' : ' products');
    none.classList.toggle('hidden', shown.length > 0);
    none.classList.toggle('flex', shown.length === 0);
    grid.classList.toggle('hidden', shown.length === 0);
    if (clearBtn) clearBtn.classList.toggle('is-on', search.value !== '');

    refreshCounts();
    refreshChips();
    syncUrl();

    if (flipState) {
      window.Flip.from(flipState, {
        duration: 0.32,
        ease: 'power2.inOut',
        absolute: true,
        onEnter: function (els) {
          return window.gsap.fromTo(els, { opacity: 0, scale: 0.96 },
            { opacity: 1, scale: 1, duration: 0.22, ease: 'power2.out' });
        },
        onLeave: function (els) {
          return window.gsap.to(els, { opacity: 0, scale: 0.96, duration: 0.16, ease: 'power2.in' });
        }
      });
    }
  }

  /* ---------- wiring ---------- */
  groups.forEach(function (g) {
    g.inputs.forEach(function (input) {
      input.addEventListener('change', function () { render(true); });
    });
  });

  var typing;
  search.addEventListener('input', function () {
    term = search.value.toLowerCase().trim();
    clearTimeout(typing);
    typing = setTimeout(function () { render(true); }, 90);   /* one beat, so a word is not 6 Flips */
    if (clearBtn) clearBtn.classList.toggle('is-on', search.value !== '');
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      search.value = ''; term = '';
      render(true);
      search.focus();
    });
  }

  sortSel.addEventListener('change', function () { render(true); });

  resets.forEach(function (r) {
    r.addEventListener('click', function () {
      search.value = ''; term = '';
      tagGroups.forEach(function (g) { g.inputs.forEach(function (i) { i.checked = false; }); });
      if (priceGroup) priceGroup.inputs[0].checked = true;
      sortSel.value = 'feat';
      render(true);
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === '/' && document.activeElement !== search) {
      var tag = (document.activeElement.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      e.preventDefault();
      search.focus();
      search.select();
    } else if (e.key === 'Escape' && document.activeElement === search && search.value !== '') {
      search.value = ''; term = '';
      render();
    }
  });

  /* the rail is a disclosure only below 1024px; above it, always open */
  function railMode() {
    if (!rail) return;
    if (window.matchMedia('(min-width: 1024px)').matches) rail.open = true;
  }
  railMode();
  window.addEventListener('resize', railMode, { passive: true });

  readUrl();
  render();
})();
